import type { Decision, EspaceOverview, EspaceProfile } from "@shared/espace.types";
import { Check, FileText, HelpCircle, Users, X } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Avatar } from "@/components/ds/Avatar";
import { Badge } from "@/components/ds/Badge";
import { Card } from "@/components/ds/Card";
import { EmptyState } from "@/components/ds/EmptyState";
import { FilterChip } from "@/components/ds/FilterChip";
import { LinkButton } from "@/components/ds/LinkButton";
import { DECISION_LABEL, relativeDate } from "./labels";

/**
 * Les profils que l'école a envoyés, et la réponse de l'entreprise.
 *
 * C'est l'écran qui justifie l'espace. Le reste (déposer, suivre) existait déjà
 * ailleurs ; celui-là est ce qu'aucune boîte mail ne sait faire — une réponse par
 * profil, que l'école lit tout de suite.
 *
 * Trois réponses et pas cinq, et surtout pas une note sur cinq : une note pousse à
 * comparer des personnes sur une échelle, quand la seule question utile est « je le
 * rencontre ou pas ». Le vocabulaire est celui du parcours de Garry.
 */

const CHOICES: { key: Decision; icon: typeof Check }[] = [
	{ key: "oui", icon: Check },
	{ key: "peut-etre", icon: HelpCircle },
	{ key: "pas", icon: X },
];

export function ProfilesPage({ overview }: { overview: EspaceOverview }) {
	if (overview.selections.length === 0) {
		return (
			<div className="flex flex-col gap-5">
				<h1 className="t-h1 text-mg-heading">Profils reçus</h1>
				<Card padded={false}>
					<EmptyState
						icon={<Users className="size-5" aria-hidden="true" />}
						title="Aucun profil reçu"
						body="Votre école envoie ici les étudiants qui correspondent à vos offres. Vous répondrez profil par profil, sans échange de mails."
						action={<LinkButton to="/espace/offres">Voir mes offres</LinkButton>}
					/>
				</Card>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-5">
			<header>
				<h1 className="t-h1 text-mg-heading">Profils reçus</h1>
				<p className="t-body mt-1 text-mg-secondary">
					{overview.selections.length === 1
						? "1 envoi de votre école"
						: `${overview.selections.length} envois de vos écoles`}
				</p>
			</header>

			<ul className="flex flex-col gap-3">
				{overview.selections.map((selection) => {
					const pending = selection.profiles.filter((profile) => profile.decision === null).length;
					const wanted = selection.profiles.filter((profile) => profile.decision === "oui").length;
					return (
						<li key={selection.id}>
							<Link
								to={`/espace/profils/${selection.id}`}
								className="block rounded-card border border-mg-border bg-mg-surface p-5 transition-shadow hover:shadow-card"
							>
								<div className="flex flex-wrap items-start justify-between gap-3">
									<div className="min-w-0">
										<p className="t-h4 text-mg-heading">{selection.title}</p>
										<p className="t-body mt-1 text-mg-secondary">
											{[selection.schoolName, selection.offerJob].filter(Boolean).join(" · ")}
										</p>
									</div>
									{pending > 0 ? (
										<Badge tone="brand">
											{pending === 1 ? "1 à regarder" : `${pending} à regarder`}
										</Badge>
									) : (
										<Badge tone="success">Répondu</Badge>
									)}
								</div>
								<div className="mt-4 flex flex-wrap items-center gap-2 border-t border-mg-border-subtle pt-3">
									{/*
									 * Côte à côte, pas en pile : une pile d'avatars marche avec
									 * des photos, pas avec des initiales — la seconde lettre
									 * passe sous le voisin, et chaque rond se lit comme une
									 * lettre tronquée.
									 */}
									<span className="flex gap-1">
										{selection.profiles.slice(0, 5).map((profile) => (
											<Avatar key={profile.id} name={profile.fullName} size="sm" />
										))}
									</span>
									<span className="t-caption text-mg-secondary">
										{selection.profiles.length} profils · reçu {relativeDate(selection.createdAt)}
										{wanted > 0 ? ` · ${wanted} à rencontrer` : ""}
									</span>
								</div>
							</Link>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

export function SelectionDetailPage({
	overview,
	onAnswer,
}: {
	overview: EspaceOverview;
	onAnswer: (profileId: string, decision: Decision | null, comment: string | null) => void;
}) {
	const { id } = useParams();
	const selection = overview.selections.find((candidate) => candidate.id === id);

	if (!selection) {
		return (
			<Card padded={false}>
				<EmptyState
					title="Cet envoi n'existe plus"
					body="Votre école l'a peut-être retiré."
					action={
						<Link to="/espace/profils" className="t-label text-mg-action hover:underline">
							Revenir aux profils
						</Link>
					}
				/>
			</Card>
		);
	}

	const answered = selection.profiles.filter((profile) => profile.decision !== null).length;

	return (
		<div className="flex flex-col gap-5">
			<Link to="/espace/profils" className="t-label text-mg-action hover:underline">
				← Profils reçus
			</Link>

			<Card>
				<h1 className="t-h2 text-mg-heading">{selection.title}</h1>
				<p className="t-body mt-1 text-mg-secondary">
					{selection.schoolName} · reçu {relativeDate(selection.createdAt)}
					{selection.offerJob ? ` · pour « ${selection.offerJob} »` : ""}
				</p>
				{selection.message ? (
					<p className="t-body mt-4 rounded-field bg-mg-hover px-4 py-3 text-mg-heading">
						{selection.message}
					</p>
				) : null}
				<p className="t-caption mt-4 border-t border-mg-border-subtle pt-3 text-mg-secondary">
					{answered} réponse{answered > 1 ? "s" : ""} sur {selection.profiles.length}. Votre école
					les voit au fur et à mesure.
				</p>
			</Card>

			<ul className="flex flex-col gap-3">
				{selection.profiles.map((profile) => (
					<li key={profile.id}>
						<ProfileCard profile={profile} onAnswer={onAnswer} />
					</li>
				))}
			</ul>
		</div>
	);
}

function ProfileCard({
	profile,
	onAnswer,
}: {
	profile: EspaceProfile;
	onAnswer: (profileId: string, decision: Decision | null, comment: string | null) => void;
}) {
	const [comment, setComment] = useState(profile.comment ?? "");
	const [writing, setWriting] = useState(false);

	// La formation n'apparaît qu'une fois : quand l'école l'a déjà mise dans
	// l'accroche, la répéter en dessous fait lire deux fois la même ligne.
	const programRepeated = Boolean(profile.program && profile.headline?.includes(profile.program));
	const facts = [
		programRepeated ? null : profile.program,
		profile.location,
		profile.availability,
	].filter(Boolean);

	return (
		<Card>
			<div className="flex items-start gap-4">
				<Avatar name={profile.fullName} size="lg" />
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-start justify-between gap-2">
						<div className="min-w-0">
							<p className="t-h4 text-mg-heading">{profile.fullName}</p>
							{profile.headline ? (
								<p className="t-body mt-0.5 text-mg-body">{profile.headline}</p>
							) : null}
						</div>
						{profile.decision ? (
							<Badge tone={DECISION_LABEL[profile.decision].tone}>
								{DECISION_LABEL[profile.decision].short}
							</Badge>
						) : null}
					</div>
					{facts.length > 0 ? (
						<p className="t-caption mt-1.5 text-mg-secondary">{facts.join(" · ")}</p>
					) : null}
					{profile.cvUrl ? (
						<a
							href={profile.cvUrl}
							target="_blank"
							rel="noreferrer"
							className="t-label mt-3 inline-flex items-center gap-1.5 text-mg-action hover:underline"
						>
							<FileText className="size-4" />
							Ouvrir le CV
						</a>
					) : null}
				</div>
			</div>

			<div className="mt-4 flex flex-wrap gap-2 border-t border-mg-border-subtle pt-4">
				{CHOICES.map((choice) => {
					const active = profile.decision === choice.key;
					return (
						<FilterChip
							key={choice.key}
							selected={active}
							icon={<choice.icon className="size-4" aria-hidden="true" />}
							onClick={() =>
								onAnswer(profile.id, active ? null : choice.key, comment.trim() || null)
							}
						>
							{DECISION_LABEL[choice.key].label}
						</FilterChip>
					);
				})}
			</div>

			{writing || profile.comment ? (
				<div className="mt-3">
					<label htmlFor={`comment-${profile.id}`} className="t-caption text-mg-secondary">
						Votre remarque, lue par l'école
					</label>
					<textarea
						id={`comment-${profile.id}`}
						rows={2}
						className="mg-field mt-1 resize-y"
						value={comment}
						onChange={(event) => setComment(event.target.value)}
						onBlur={() => onAnswer(profile.id, profile.decision, comment.trim() || null)}
					/>
				</div>
			) : (
				<button
					type="button"
					onClick={() => setWriting(true)}
					className="t-caption mt-3 text-mg-action hover:underline"
				>
					Ajouter une remarque
				</button>
			)}
		</Card>
	);
}
